import CounterInput from "@components/common/CounterInput";
import MultiRangePrice from "@components/common/requestQuote/MultiRangePrice";
import MediaAssets from "@components/utils/MediaAssets";
import { ImageDefault } from "@components/utils/Utils";
import { BUDGETS } from "@components/utils/constants";
import { ITopNavigationResponse } from "@customTypes/commonResponses";
import { COUNT, TYPE } from "@customTypes/index";
import useCountInput, { CountAction, CountType } from "hooks/useCountInput";
import Image from "next/legacy/image";
import { useCallback, useEffect, useMemo, useState } from "react";
import ExitButton from '@components/icons/ExitButton'

interface IDesktopModalProps {
  onRequestOpen: (isOpen: boolean) => void;
  onClick: (isOpen: boolean) => void;
  topNavigation?: ITopNavigationResponse;
}

const MIN = 0;
const MAX = 1;

function DesktopModal({
                        onRequestOpen,
                        onClick,
                        topNavigation,
                      }: IDesktopModalProps): JSX.Element {
  const {
    numberOfAdult,
    numberOfTeen,
    numberOfChildren,
    setNumberOfAdult,
    setNumberOfChildren,
    setNumberOfTeen,
    handleTypeCount,
  } = useCountInput();

  const currentBudgetRanges = useMemo(() => {
    return [BUDGETS.MIN, BUDGETS.MAX];
  }, []);

  const [minBudget, setMinBudget] = useState(
    typeof window !== "undefined" && localStorage.getItem("USER_SEARCH_FILTERS")
      ? localStorage.getItem("USER_REQUEST_QUOTE")
        ? // @ts-expect-error
        JSON.parse(localStorage.getItem("USER_REQUEST_QUOTE")).budgets.min
        : // @ts-expect-error
        JSON.parse(localStorage.getItem("USER_SEARCH_FILTERS")).accommodations
          .budget.min
      : BUDGETS.MIN
  );
  const [maxBudget, setMaxBudget] = useState(
    typeof window !== "undefined" && localStorage.getItem("USER_SEARCH_FILTERS")
      ? localStorage.getItem("USER_REQUEST_QUOTE")
        ? // @ts-expect-error
        JSON.parse(localStorage.getItem("USER_REQUEST_QUOTE")).budgets.max
        : // @ts-expect-error
        JSON.parse(localStorage.getItem("USER_SEARCH_FILTERS")).accommodations
          .budget.max
      : BUDGETS.MAX
  );

  const onIncrementClick = useCallback(
    (count: CountAction, type: CountType) => {
      handleTypeCount(count, type);
    },
    [numberOfAdult, numberOfTeen, numberOfChildren]
  );
  const onDecrementClick = useCallback(
    (count: CountAction, type: CountType) => {
      handleTypeCount(count, type);
    },
    [numberOfAdult, numberOfTeen, numberOfChildren]
  );

  const onChange = (ranges: any): void => {
    setMinBudget(ranges[MIN]);
    setMaxBudget(ranges[MAX]);
  };

  useEffect(() => {
    let CURRENT_STATE = localStorage.getItem("USER_REQUEST_QUOTE");
    if (CURRENT_STATE !== null) {
      CURRENT_STATE = JSON.parse(CURRENT_STATE);
      const budgets = {
        min: minBudget,
        max: maxBudget,
      };
      // @ts-expect-error
      CURRENT_STATE.budgets = budgets;
      localStorage.setItem("USER_REQUEST_QUOTE", JSON.stringify(CURRENT_STATE));
    }
  }, [minBudget, maxBudget]);

  useEffect(() => {
    if (localStorage.getItem("USER_REQUEST_QUOTE")) {
      const { adults, teens, children } = JSON.parse(
        // @ts-expect-error
        localStorage.getItem("USER_REQUEST_QUOTE")
      ).travelers;

      setNumberOfAdult(adults);
      setNumberOfTeen(teens);
      setNumberOfChildren(children);
    }
  }, []);

  return (
    <div className="md:inset-0 md:h-full fixed top-0 left-0 right-0 z-[101] h-[100%] w-full overflow-y-auto overflow-x-hidden">
      <div className="md:h-auto relative h-[100%] w-full">
        <div className="shadow dark:bg-gray-700 relative h-[100%] rounded-lg bg-white">
          <div className="space-y-6 p-6">
            <div className="items-top flex overflow-y-auto overflow-x-hidden bg-white">
              <div className="relative aspect-auto h-[calc(100vh_-_3rem)] w-[45%]">
                <Image
                  src={MediaAssets.getAsset("raq_step_2")}
                  layout="fill"
                  className="h-full w-full rounded-3xl object-cover"
                  alt="request a quote step 2"
                />
              </div>
              <div className="flex h-[calc(100vh_-_3rem)] w-[55%] flex-col">
                <div className="ml-20 flex h-[calc(100vh_-_3rem)] flex-col justify-between">
                  <div className="flex w-full items-center justify-between">
                    <div>
                      <Image
                        src={ImageDefault(topNavigation?.Logo)}
                        width="142"
                        height="48"
                        layout="fixed"
                        alt="Costa Rican Vacations Logo"
                      />
                    </div>
                    <div>
                      <ExitButton onClick={onRequestOpen}/>
                    </div>
                  </div>
                  <div className="w-[424px]">
                    <h1 className="mb-[4.25rem] text-h4 font-medium">
                      Request a Custom Quote
                    </h1>
                    <div className="flex">
                      <h3 className="mb-[6px] font-medium text-darkgray">
                        Number of Travelers
                      </h3>
                    </div>
                    <div className="mb-6 flex justify-between">
                      <div className="flex flex-col">
                        <div className="mb-[0.5] font-medium">Adults</div>
                        <div className="text-p13 font-medium text-midgray">
                          Ages 18 and above
                        </div>
                      </div>
                      <CounterInput
                        value={numberOfAdult}
                        onIncrementClick={() =>
                          onIncrementClick(COUNT.increment, TYPE.adult)
                        }
                        onDecrementClick={() =>
                          onDecrementClick(COUNT.decrement, TYPE.adult)
                        }
                      />
                    </div>
                    <div className="mb-6 flex justify-between">
                      <div className="flex flex-col">
                        <div className="mb-[0.5] font-medium">Teens</div>
                        <div className="text-p13 font-medium text-midgray">
                          Ages 12 to 17
                        </div>
                      </div>
                      <CounterInput
                        value={numberOfTeen}
                        onIncrementClick={() =>
                          onIncrementClick(COUNT.increment, TYPE.teen)
                        }
                        onDecrementClick={() =>
                          onDecrementClick(COUNT.decrement, TYPE.teen)
                        }
                      />
                    </div>
                    <div className="mb-6 flex justify-between">
                      <div className="flex flex-col">
                        <div className="mb-[0.5] font-medium">Children</div>
                        <div className="text-p13 font-medium text-midgray">
                          Under 12
                        </div>
                      </div>
                      <CounterInput
                        value={numberOfChildren}
                        onIncrementClick={() =>
                          onIncrementClick(COUNT.increment, TYPE.children)
                        }
                        onDecrementClick={() =>
                          onDecrementClick(COUNT.decrement, TYPE.children)
                        }
                      />
                    </div>

                    <div className="mt-8 mb-4 flex">
                      <h3 className="mb-[6px] font-medium text-darkgray">
                        Nightly budget
                      </h3>
                    </div>
                    <div
                      className="desktop:shadow-none h-60 items-center
                      justify-center rounded-lg bg-[#FFF]"
                    >
                      <MultiRangePrice/>

                      <div className="mb-5 mt-[0.625rem] flex items-center justify-between pr-6">
                        <div className="text-p13 text-darkgray">{`$${currentBudgetRanges[0]}`}</div>
                        <div className="text-p13 text-darkgray">{`$${currentBudgetRanges[1]
                          .toString()
                          .substring(0, 1)},${currentBudgetRanges[1]
                          .toString()
                          .substring(1)}+`}</div>
                      </div>
                      <button
                        onClick={onClick as any}
                        className="mx-auto flex w-[424px] justify-center rounded-full bg-orange p-[1.375rem] font-semibold text-white"
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                  <div className="h-[48px]"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* <div className="absolute top-[2.175rem] right-3">
        <ExitButton onClick={onRequestOpen} />
      </div> */}
    </div>
  );
}

export default DesktopModal;
